// Header bell for write proposals that arrived through the MCP (the agent's
// external Claude). This is the ONLY place such a proposal gets executed: the
// human reads the exact summary and clicks Confirmar under their browser
// session. The MCP itself can never confirm (see routes/mcp.js).
//
// Polls GET /nl/actions/mine every 30s (cheap, index-backed) and only renders
// when there is something pending — so agents without an API token never see it.
// Approvers also see bot service accounts' proposals here (badge "Propuesto por
// <bot>", 24 h lifetime); confirming runs them under the approver's session.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Bot, Check, X, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const POLL_MS = 30 * 1000;

export default function PendingActionsBell() {
  const { t } = useTranslation();
  const [proposals, setProposals] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const btnRef = useRef(null);
  const [rect, setRect] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/nl/actions/mine');
      setProposals(Array.isArray(data?.proposals) ? data.proposals : []);
    } catch { /* silent: telemetry-grade poll */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (open && btnRef.current) setRect(btnRef.current.getBoundingClientRect());
  }, [open]);

  const act = async (p, kind) => {
    setBusy(p.id);
    try {
      if (kind === 'confirm') {
        const { data } = await api.post(`/nl/actions/${p.id}/confirm`);
        toast.success(data?.message || t('pendingActions.executed'));
      } else {
        await api.post(`/nl/actions/${p.id}/cancel`);
        toast.success(t('pendingActions.cancelled'));
      }
    } catch (e) {
      const err = e?.response?.data?.error;
      toast.error(err === 'expired' ? t('pendingActions.expired') : err === 'actions_cap' ? t('pendingActions.cap') : t('common.actionFailed'));
    } finally {
      setBusy(null);
      load();
    }
  };

  if (!proposals.length) return null;

  const panel = open && rect ? ReactDOM.createPortal(
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1000 }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="card"
        style={{
          position: 'fixed', top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right),
          width: 'min(440px, calc(100vw - 16px))', margin: 0, padding: 12, maxHeight: '70vh', overflowY: 'auto',
          boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Bot size={18} />
          <strong>{t('pendingActions.title')}</strong>
        </div>
        <p style={{ color: 'var(--c-text-muted)', fontSize: 12, margin: '0 0 10px' }}>{t('pendingActions.subtitle')}</p>
        {proposals.map(p => (
          <div key={p.id} style={{ border: '1px solid var(--c-border)', borderRadius: 8, padding: 10, marginBottom: 8, background: 'var(--c-surface-2)' }}>
            {p.from_bot && (
              <div style={{ marginBottom: 6 }}>
                <span className="badge badge-pending" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Bot size={11} /> {t('pendingActions.proposedBy', { name: p.proposed_by || '—' })}
                </span>
              </div>
            )}
            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{p.summary}</div>
            {p.nl_query && <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginTop: 4 }}>{t('pendingActions.reason')}: {p.nl_query}</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--c-text-muted)', display: 'flex', alignItems: 'center', gap: 4, flex: 1 }}>
                <Clock size={11} /> {new Date(p.created_at).toLocaleString('es-AR', p.from_bot ? { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' } : { hour: '2-digit', minute: '2-digit' })}
              </span>
              <button className="btn btn-ghost btn-sm" disabled={busy === p.id} onClick={() => act(p, 'cancel')}>
                <X size={14} /> {t('common.cancel')}
              </button>
              <button className="btn btn-primary btn-sm" disabled={busy === p.id} onClick={() => act(p, 'confirm')}>
                <Check size={14} /> {busy === p.id ? t('common.loading') : t('pendingActions.confirm')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={btnRef}
        className="notification-bell"
        onClick={() => setOpen(o => !o)}
        title={t('pendingActions.title')}
        aria-label={t('pendingActions.title')}
        style={{ color: 'var(--c-pending)' }}
      >
        <Bot size={18} />
        <span className="notification-badge">{proposals.length}</span>
      </button>
      {panel}
    </>
  );
}
